// Geometry for placing the SlotInlineEditor popover. Pulled out of the
// component as a pure function so the flip-and-clamp logic is testable without
// a DOM, and so the old "reserve a hardcoded 320px" guess — which spilled the
// Remove-shift footer off-screen on the last rows of the month — can't return.

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface PopoverPosition {
  top: number;
  left: number;
}

const MARGIN = 8; // min gap to any viewport edge
const GAP = 4; // gap between the anchor and the popover

// Given the anchor's rect, the popover's measured size, and the viewport,
// return the fixed-position top/left. Prefers opening below the anchor; flips
// above when it would overflow the bottom; clamps fully into view when it fits
// neither side (the CSS max-height caps the height, so the list scrolls and the
// footer stays visible).
export function computePopoverPosition(
  anchor: AnchorRect,
  popoverW: number,
  popoverH: number,
  viewport: Viewport,
): PopoverPosition {
  const left = Math.max(MARGIN, Math.min(anchor.left, viewport.width - popoverW - MARGIN));

  const spaceBelow = viewport.height - anchor.bottom - GAP - MARGIN;
  const spaceAbove = anchor.top - GAP - MARGIN;

  let top: number;
  if (popoverH <= spaceBelow) {
    top = anchor.bottom + GAP; // fits below
  } else if (popoverH <= spaceAbove) {
    top = anchor.top - GAP - popoverH; // flip above
  } else {
    // Fits neither side fully — place on the roomier one; the final clamp pulls
    // it wholly into view (height is already capped by CSS max-height).
    top = spaceBelow >= spaceAbove ? anchor.bottom + GAP : anchor.top - GAP - popoverH;
  }

  top = Math.min(top, viewport.height - popoverH - MARGIN);
  top = Math.max(top, MARGIN);
  return { top, left };
}
